/**
 * **Overcharge** — the mech reactor push.
 *
 * RAW (Core Book p.78): **once per turn**, a mech may make any Quick action as a **free
 * action** (even one already taken this turn) by paying escalating self-heat:
 *
 *   1st Overcharge → take **1** heat. 2nd → **1d3**. 3rd → **1d6**. 4th and each thereafter → **1d6+4**.
 *   *A full repair resets this counter.*
 *
 * **This module is the resolution half** — the rules-side producer of the recorded
 * `apply_resolved` step list (draw-and-record). Unlike Stabilize (deterministic), Overcharge
 * **draws a die** for steps 2–4, so it takes a {@link DiceSource}: the heat amount is rolled at
 * the actor's current escalation step and fed through the **shared heat / Overheating cascade**
 * ({@link resolveHeatCascade}) — Overcharge does NOT special-case heat, so an Overcharge that
 * crosses Heat Cap into Overheating draws the overheating table and records its consequences
 * exactly like any other heat source (intended).
 *
 * It reuses the existing models, it does not fork them:
 *   - the escalation step is a **per-combatant resource** ({@link OVERCHARGE_COUNT_RESOURCE}, the
 *     same generic resource bag Repair Cap rides) — read here, advanced by a recorded
 *     `resource_delta +1` step (absent ⇒ step 0 ⇒ the first Overcharge takes 1);
 *   - the self-heat flows through {@link resolveHeatCascade} (heat_delta + the overheating cascade);
 *   - the **+1 Quick action** grant + the once-per-turn gate are the room's job (the
 *     `grant_overcharge` command folded alongside this, gated by `checkOverchargeLegality`).
 *
 * Self-target only (you push your own reactor) and no player choices, so there is no
 * `InputSource` / prompt round-trip — the engine draws-and-records automatically.
 */

import type { Combatant, EncounterState, ResolvedStep, ApplyResolvedCommand } from "@lancer-vtt/domain";
import { OVERCHARGE_COUNT_RESOURCE } from "@lancer-vtt/domain";
import type { RequestOvercharge } from "@lancer-vtt/shared";
import type { DiceSource } from "../dice";
import { resolveHeatCascade } from "./tables";
import { selfCheckContext } from "./selfCheck";
import { NeedsDecisionSignal, promptingChoices, recordedChoices } from "./effects/choices";
import type { LoadedOverlay } from "./effects/loader";
import { ResolveError, type Resolved, type ResolveResult } from "./resolve";
import { spendStepsNow } from "./events/gameEvents";
import type { EventLoop } from "./events/loop";

/**
 * The self-heat an Overcharge takes at escalation `step` (0-based: the count of prior
 * Overcharges since the last full repair), drawing from `dice` for the rolled steps:
 *   - **step 0** → **1** (fixed — no draw).
 *   - **step 1** → **1d3**.
 *   - **step 2** → **1d6**.
 *   - **step 3+** → **1d6+4** (every Overcharge thereafter).
 *
 * Pure over the injected dice (a scripted source makes it deterministic in tests). The die is
 * **drawn here**, in the authority, and its *effect* recorded downstream as `heat_delta` step(s).
 */
export function overchargeHeatForStep(step: number, dice: DiceSource): number {
  if (step <= 0) return 1;
  if (step === 1) return dice.roll(3);
  if (step === 2) return dice.roll(6);
  return dice.roll(6) + 4;
}

/** The escalation step (0-based) a combatant will Overcharge AT next (absent resource ⇒ 0). */
export function overchargeStep(combatant: Combatant): number {
  return combatant.current.resources?.[OVERCHARGE_COUNT_RESOURCE] ?? 0;
}

/** Named arguments for {@link resolveOvercharge}. */
export type ResolveOverchargeArgs = {
  state: EncounterState;
  intent: RequestOvercharge;
  /** The authority dice — the heat amount (steps 2–4) and the overheating cascade draw from it. */
  dice: DiceSource;
  /** Who the resolution is attributed to (the issuing slot); stamped on the command. Defaults to `"authority"`. */
  actorId?: string;
  /** The room's overlay, which the Overheating table's ENGINEERING check folds the actor's save/check modifiers from. */
  overlay?: LoadedOverlay;
  /** The live round-trip: an unanswered reroll offer (Reactor Stabilizer) halts and asks. */
  interactive?: boolean;
  /** The intent's event loop: the overheating check's rolls raise into it. */
  loop?: EventLoop;
};

/**
 * Resolve a {@link RequestOvercharge} into ONE recorded `apply_resolved` (`kind: "overcharge"`):
 * roll the self-heat at the actor's current escalation step, run it through the heat /
 * Overheating cascade, and advance the escalation counter by one (`resource_delta +1`). The room
 * folds this with a `grant_overcharge` (the +1 Quick action) into one atomic, undoable batch.
 *
 * The once-per-turn / mech-only / on-turn legality is the room's pre-resolve wall
 * (`checkOverchargeLegality`); this assumes it already passed.
 *
 * @throws {ResolveError} if the actor id is unknown in `state`.
 */
export function resolveOvercharge(args: ResolveOverchargeArgs): ResolveResult {
  const { state, intent, dice, actorId = "authority", overlay, interactive, loop } = args;
  const actor = state.combatants[intent.actor];
  if (!actor) throw new ResolveError(`resolveOvercharge: unknown combatant ${intent.actor}`);

  const step = overchargeStep(actor);
  const heat = overchargeHeatForStep(step, dice);
  // Self-heat through the shared path — heat_delta + the overheating cascade when it crosses
  // the Heat Cap (the cascade draws its own d6s from the same authority dice). Not special-cased.
  const choices = interactive ? promptingChoices(intent.choices) : recordedChoices(intent.choices);
  let cascade: ReturnType<typeof resolveHeatCascade>;
  try {
    cascade = resolveHeatCascade({ target: actor, amount: heat, dice, turnOrdinal: state.activation.turnOrdinal, selfCheck: selfCheckContext(state, overlay, { choices, loop }) });
  } catch (sig) {
    if (!(sig instanceof NeedsDecisionSignal)) throw sig;
    return { kind: "needs_decision", who: sig.who ?? actor.id, promptId: sig.promptId, prompt: sig.prompt, options: sig.options, answerKind: sig.answerKind };
  }
  // Advance the escalation step (the next Overcharge costs more): a counter cost (`event-engine-costs`
  // D1, 5.3), recorded + undoable.
  const advance = spendStepsNow([{ kind: "counter", carrier: actor.id, key: OVERCHARGE_COUNT_RESOURCE, delta: 1 }], state, undefined);
  const steps: ResolvedStep[] = [...cascade.steps, ...advance];

  const command: ApplyResolvedCommand = { type: "apply_resolved", actorId, steps, kind: "overcharge" };
  const resolved: Resolved = { kind: "resolved", command, steps, warnings: cascade.warnings };
  return resolved;
}
